/**
 * Main-app react-router route paths — the single source of truth shared by the
 * twin's own router/links AND the satellite apps' cross-origin `MainAppNav`.
 *
 * These three routes are referenced from two independently-built bundles (the
 * main frontend and each `apps/<name>` satellite). A rename in one place that
 * isn't mirrored in the other silently 404s bedside users with no `tsc`/test
 * signal — the documented contract-drift fingerprint. Defining them once here
 * makes a rename a single-location change that every consumer picks up.
 *
 * Scope: these are the *navigation* (react-router) paths only. They are
 * deliberately NOT the backend API paths in `src/api/endpoints/*` — those share
 * some literals (`/patients`, `/documents`) but are a separate vocabulary (the
 * `/api/v1` gateway contract) and must not be coupled to UI routing.
 */
export const MAIN_APP_ROUTES = {
  home: '/home',
  documents: '/documents',
  adminAnalytics: '/admin/analytics',
} as const;

/**
 * The segment under /home that addresses a screen INSIDE an agent's frame:
 * `/home/a/<slug>/<the agent's own path>`.
 *
 * An agent's face is one screen; the screens under it (a quiz run, a curriculum
 * path) are the agent's own route tree, declared in `apps/<slug>/src/routes.tsx`
 * and mounted by AgentFrame. They live in the real URL rather than in pane
 * state for the ordinary reasons: reload and the browser Back button keep
 * working, and a nurse can send a colleague the screen she is looking at.
 *
 * One segment, not the slug alone, so an agent can never claim a main-app path
 * by being named after it.
 */
export const AGENT_FRAME_SEGMENT = 'a';

/** `/home/a/<slug>` — where an agent's own routes are mounted in the frame. */
export function agentFrameBasePath(agentSlug: string): string {
  return `${MAIN_APP_ROUTES.home}/${AGENT_FRAME_SEGMENT}/${agentSlug}`;
}

const AGENT_FRAME_PATH = new RegExp(
  `^${MAIN_APP_ROUTES.home}/${AGENT_FRAME_SEGMENT}/([^/]+)`,
);

/**
 * The agent a path addresses in the frame, or null for a plain Home path.
 *
 * A URL naming a screen inside an agent is an instruction about what the pane
 * shows — reload, Back and a shared link all arrive that way, and they have to
 * beat the selection this browser happened to have last.
 */
export function agentSlugFromFramePath(pathname: string): string | null {
  const m = AGENT_FRAME_PATH.exec(pathname);
  return m ? m[1] : null;
}

/**
 * The agent's OWN path for a frame URL: `/home/a/<slug>/studio/x/v1` →
 * `/studio/x/v1`. The inverse of agentFrameBasePath, so the screen a nurse is
 * on in the frame names the same screen on the agent's full-screen surface,
 * where the agent IS the app and its paths sit at the root. A pathname outside
 * this agent's frame is its root, `/`.
 */
export function agentPathFromFramePath(agentSlug: string, pathname: string): string {
  const base = agentFrameBasePath(agentSlug);
  if (pathname === base) return '/';
  return pathname.startsWith(`${base}/`) ? pathname.slice(base.length) : '/';
}
